import { AnalyticalItemAction } from 'api/betslipAnalytics.api';
import { AICampaignOption } from 'features/AICampaign/interface';
import { WEEK_IN_MS } from 'shared/constants/common';
import { DATALAYER, DataLayerEventList, dataLayerPusher } from '../dataLayer';
import { getDeviceType } from '../hooks/useDeviceManager';

export const setDataLayer = (dataLayerObj: object) => {
	const device = getDeviceType();
	DATALAYER.push({ ...dataLayerObj, ...device });
};

export const setDataLayerWithCallback = async (dataLayerObj: object, timeout = 1000) => {
	return new Promise<void>((resolve) => {
		if (!(window as any).google_tag_manager) {
			resolve();
			return;
		}

		let timedOut = false;
		const timeoutId = setTimeout(() => {
			timedOut = true;
			resolve();
		}, timeout);

		const device = getDeviceType();

		DATALAYER.push({
			...dataLayerObj,
			...device,
			eventCallback: () => {
				if (!timedOut) {
					clearTimeout(timeoutId);
					resolve();
				}
			},
			eventTimeout: timeout,
		});
	});
};

export const DATALAYER_REGISTER_FAIL = {
	event: 'register-failed',
};

export const DATALAYER_PLACEBET_SUCCESS = {
	event: 'placeBet-success',
};

export type DataLayerEvent = {
	event: string;
} & Record<string, any>;

const createEvent = (event: string, additionalParam?: object): DataLayerEvent => {
	if (additionalParam !== undefined) {
		return { event, ...additionalParam };
	}

	return { event };
};

const createBetslipManipulationEvent = (event: string, additionalParam?: object) => {
	if (typeof additionalParam == 'undefined') return createEvent(event);

	const session_id = getBetslipSessionId();
	const params = { ...additionalParam, session_id };
	return createEvent(event, params);
};

const getBetslipSessionId = () => {
	const now = new Date().getTime();

	// Retrieve the current session data, if any
	const currentSession = JSON.parse(localStorage.getItem('betslipSession') || '{}');
	const { sessionId, ttl } = currentSession;

	// Check if the current session is valid
	const isSessionValid = ttl && ttl >= now;

	// Generate a new session ID if needed
	const newSessionId = isSessionValid ? sessionId : window.crypto.randomUUID();

	// Update the session data
	const updatedSession = {
		sessionId: newSessionId,
		ttl: now + WEEK_IN_MS,
	};
	localStorage.setItem('betslipSession', JSON.stringify(updatedSession));

	return newSessionId;
};

const removeBetslipSessionId = () => {
	localStorage.removeItem('betslipSession');
};

type DataLayerBetType = {
	coupon_events?: AnalyticalItemAction[];
	coupon_events_removed?: AnalyticalItemAction[];
	origin_url?: string;
	bet_value?: number;
	total_events?: number;
	total_events_removed?: number;
	bet_balance_type: string;
	coupon_type: string;
	event_id: string[] | number[];
	participants: string[];
	event_category_1: string[];
	event_category_2: string[];
	bet_selected_1: string[];
	bet_selected_2: string[];
	outcome_url: string;
	outcome_from_homepage: boolean;
	bet_odds: string[];
	total_odds: number;
	bet_promo: string;
	bet_type: string | number;
	bet_live: boolean[];
	bet_potential_win: string | number;
};

type DataLayerBetSuccess = {
	is_premade_betbuilder: boolean;
	balance_before_coupon: number;
	balance_after_coupon: number;
	bet_bonus_balance?: number;
	bet_balance_type: string;
	coupon_type: string;
	stake_method: string;
	bet_value: number;
	origin_url: string;
	total_events: number;
	total_events_removed: number;

	bet_potential_win: string | number;
	bet_type: string | number;
	total_odds: number;
	coupon_events?: AnalyticalItemAction[];
	coupon_events_removed?: AnalyticalItemAction[];
};

export const BetslipEvents = {
	BET_START: (values: DataLayerBetType) => createBetslipManipulationEvent('bet_start', values),
	BET_RESUME: (values: DataLayerBetType) => createBetslipManipulationEvent('bet_resume', values),
	BET_ADD_NEXT_EVENT: (values: DataLayerBetType, event_count: number) =>
		createBetslipManipulationEvent('bet_add_next_event', { ...values, event_count }),
	BET_REMOVE: (values: DataLayerBetType, betRemoveEventCount: number) =>
		createBetslipManipulationEvent('bet_remove', { ...values, betRemoveEventCount }),
	BET_DISABLE: (values: DataLayerBetType, betDisableEventCount: number) => createEvent('bet_disable', { ...values, betDisableEventCount }),
	BET_ENABLE: (values: DataLayerBetType, betEnableEventCount: number) => createEvent('bet_enable', { ...values, betEnableEventCount }),
	BET_REPLACE: (values: DataLayerBetType, betReplaceEventCount: number) => createEvent('bet_replace', { ...values, betReplaceEventCount }),
	BET_AMOUNT_LAST: (values: DataLayerBetType, bet_value: number) => createEvent('bet_amount_last', { ...values, bet_value }),
	BET_AMOUNT_ALLIN: (values: DataLayerBetType, bet_value: number) => createEvent('bet_amount_allin', { ...values, bet_value }),
	BET_AMOUNT_DEFINED: (values: DataLayerBetType, bet_value_defined: number) => createEvent('bet_amount_defined', { ...values, bet_value_defined }),
	BET_AMOUNT_MANUAL: (values: DataLayerBetType, bet_value_manual: number) => createEvent('bet_amount_manual', { ...values, bet_value_manual }),
	BET_AMOUNT_CANCEL: (values: DataLayerBetType) => createEvent('bet_amount_cancel', values),
	BET_AMOUNT_CONFIRM: (values: DataLayerBetType) => createEvent('bet_amount_confirm', values),
	BET_SUCCESS: (values: DataLayerBetSuccess) => {
		const event = createBetslipManipulationEvent('bet_success', { ...values });
		removeBetslipSessionId();
		return event;
	},
	BET_CANCEL: (values: DataLayerBetType) => {
		const event = createBetslipManipulationEvent('bet_cancel', values);
		removeBetslipSessionId();
		return event;
	},
	BET_ABANDON: (values: DataLayerBetType) => {
		const event = createBetslipManipulationEvent('bet_abandon', values);
		removeBetslipSessionId();
		return event;
	},
	BET_AGAIN: (values: DataLayerBetType) => {
		removeBetslipSessionId();
		return createBetslipManipulationEvent('bet_again', values);
	},
	BET_FINISHED: (values: DataLayerBetType) => createEvent('bet_finished', values),
	BET_WIN: (amount: string) => createEvent('bet_win', { amount }),
	BETBUILDER_RATING: (rating: 'bad' | 'good' | 'super') => createEvent('betbuilder_rating', { betbuilder_rating_value: rating }),
};

export const DepositEvents = {
	DEPOSIT_FROM_HEADER: createEvent('depo_start_1'),
	DEPOSIT_FROM_CUSTOMER_PANEL: createEvent('depo_start_2'),
	ENTER_DEPOSIT_AMOUNT_MANUAL: (depo_amount_manual: number) => createEvent('depo_amount_manual', { depo_amount_manual }),
	ENTER_DEPOSIT_AMOUNT_PREDEFINED: (depo_amount_defined: number) => createEvent('depo_amount_defined', { depo_amount_defined }),
	DEPOSIT_VIA_BLIK: createEvent('payment_method_1'),
	DEPOSIT_SAVE_BLIK: createEvent('save_method_1'),
	DEPOSIT_VIA_P24: createEvent('payment_method_2'),
	DEPOSIT_VIA_CARD: createEvent('payment_method_3'),
	DEPOSIT_SAVE_CARD: createEvent('save_method_3'),
	DEPOSIT_START: createEvent('payment_start'),
	FIRST_TIME_DEPOSIT: (deposit_value: number) => createEvent('first_time_deposit', { deposit_value }),
	SECOND_TIME_DEPOSIT: (deposit_value: number) => createEvent('second_time_deposit', { deposit_value }),
	THIRD_TIME_DEPOSIT: (deposit_value: number) => createEvent('third_time_deposit', { deposit_value }),
	PAYMENT_SUCCESS_TEST: createEvent('payment_success_test'),
	DEPOSIT_SUCCESS_TEST: createEvent('deposit_success_test'),
	DEPOSIT_FALSE: dataLayerPusher(DataLayerEventList.DepositFalse),
};

export type RegistrationType = 'manual' | 'auto';
export type VerificationType = RegistrationType;
export type ActivationType = 'email' | 'phone';

export const RegisterEvents = {
	DATALAYER_RELOGIN_START: createEvent('relogin_start'),
	DATALAYER_REGISTER_START: (registrationType: RegistrationType) => createEvent('reg_start', { reg_type: [registrationType] }),
	DATALAYER_REGISTER_START_FROM_BANNER: (reg_alt: string) => createEvent('reg_start2', { reg_alt }),
	DATALAYER_REGISTER_SUCCESS: (registrationType: RegistrationType) => createEvent('reg_success', { reg_type: [registrationType] }),
	DATALAYER_REGISTER_FAILURE: (registrationType: RegistrationType) => createEvent('reg_failure', { reg_type: [registrationType] }),
	DATALAYER_REGISTER_ACCOUNT_INACTIVE: (registrationType: RegistrationType) =>
		createEvent('reg_account_inactive', { reg_type: [registrationType] }),
	REGISTRATION_1_BACK: (registrationType: RegistrationType) => createEvent('reg_0back', { reg_type: [registrationType] }),
	REGISTRATION_1_FINISHED: (registrationType: RegistrationType) => createEvent('reg_1step_completed', { reg_type: [registrationType] }),
	REGISTRATION_2_BACK: (registrationType: RegistrationType) => createEvent('reg_1back', { reg_type: [registrationType] }),
	REGISTRATION_2_FINISHED: (registrationType: RegistrationType) => createEvent('reg_2step_completed', { reg_type: [registrationType] }),
	REGISTRATION_3_BACK: (registrationType: RegistrationType) => createEvent('reg_2back', { reg_type: [registrationType] }),
	REGISTRATION_3_FINISHED: (userEmail: string, registrationType: RegistrationType) =>
		createEvent('reg_3step_completed', { userEmail, reg_type: registrationType }),
	REGISTRATION_4_BACK: (registrationType: RegistrationType) => createEvent('reg_3back', { reg_type: [registrationType] }),
	REGISTRATION_4_FINISHED: (registrationType: RegistrationType) => createEvent('reg_4step_completed', { reg_type: [registrationType] }),
	REGISTRATION_5_BACK: (registrationType: RegistrationType) => createEvent('reg_4back', { reg_type: [registrationType] }),
	REGISTRATION_5_FINISHED: (registrationType: RegistrationType) => createEvent('reg_5step_completed', { reg_type: [registrationType] }),
	REGISTRATION_6_BACK: (registrationType: RegistrationType) => createEvent('reg_5back', { reg_type: [registrationType] }),
	REGISTRATION_6_FINISHED: (activation_type: ActivationType, registrationType: RegistrationType) =>
		createEvent('reg_6step_completed', { activation_type, reg_type: registrationType }),
	REGISTRATION_7_BACK: (registrationType: RegistrationType) => createEvent('reg_6back', { reg_type: [registrationType] }),
	REGISTRATION_7_FINISHED: (registrationType: RegistrationType) => createEvent('reg_7step_completed', { reg_type: [registrationType] }),
	REGISTRATION_8_BACK: (registrationType: RegistrationType) => createEvent('reg_7back', { reg_type: [registrationType] }),
	REGISTRATION_8_FINISHED: (registrationType: RegistrationType) => createEvent('reg_8step_completed', { reg_type: [registrationType] }),
	REGISTRATION_9_BACK: (registrationType: RegistrationType) => createEvent('reg_8back', { reg_type: [registrationType] }),
	REGISTRATION_9_FINISHED: (
		activation_type: ActivationType,
		marketing_checkbox: boolean,
		reg_scratch: boolean,
		consent_count_before: number | undefined,
		consent_count_after: number,
		consent_popup: boolean,
		registrationType: RegistrationType,
	) =>
		createEvent('reg_9step_completed', {
			activation_type,
			marketing_checkbox,
			reg_scratch,
			consent_count_before,
			consent_count_after,
			consent_popup,
			reg_type: registrationType,
		}),
	REGISTRATION_MARKETING_CONSENTS: (consent_value: boolean, registrationType: RegistrationType) =>
		createEvent('marketing_consent', { consent_value, reg_type: registrationType }),
};

export const LoginEvents = {
	DATALAYER_LOGIN_START: createEvent('login_start'),
	DATALAYER_LOGIN_SUCCESS: createEvent('login_success'),
	DATALAYER_FIRSTLOGIN_START: createEvent('firstlogin_start'),
	DATALAYER_FIRSTLOGIN_SUCCESS: createEvent('firstlogin_success'),
};

export const AvatarsEvents = {
	DATALAYER_AVATAR_CHANGE: createEvent('avatar_change'),
	DATALAYER_AVATAR_SET: createEvent('avatar_set'),
};

export const VerificationEvents = {
	DATALAYER_VERIFICATION_FORM_START: createEvent('verify_1start'),
	VERIFICATION_1_BACK: (verificationType: VerificationType) => createEvent('verify_1back', { reg_type: [verificationType] }),
	VERIFICATION_1_FINISHED: (verificationType: VerificationType) => createEvent('verify_1step_completed', { reg_type: [verificationType] }),
	VERIFICATION_2_BACK: (verificationType: VerificationType) => createEvent('verify_2back', { reg_type: [verificationType] }),
	VERIFICATION_2_FINISHED: (verificationType: VerificationType) => createEvent('verify_2step_completed', { reg_type: [verificationType] }),
	VERIFICATION_3_BACK: (document_type: string, verificationType: VerificationType) =>
		createEvent('verify_3back', { document_type, reg_type: verificationType }),
	VERIFICATION_3_FINISHED: (verificationType: VerificationType) => createEvent('verify_3step_completed', { reg_type: [verificationType] }),
	VERIFICATION_4_BACK: (verificationType: VerificationType) => createEvent('verify_4back', { reg_type: [verificationType] }),
	VERIFICATION_4_FINISHED: (verificationType: VerificationType) => createEvent('verify_4step_completed', { reg_type: [verificationType] }),
	VERIFICATION_5_BACK: (verificationType: VerificationType) => createEvent('verify_5back', { reg_type: [verificationType] }),
	VERIFICATION_5_FINISHED: (verificationType: VerificationType) => createEvent('verify_5step_completed', { reg_type: [verificationType] }),
	VERIFICATION_6_BACK: (verificationType: VerificationType) => createEvent('verify_6back', { reg_type: [verificationType] }),
	VERIFICATION_6_FINISHED: (verificationType: VerificationType) => createEvent('verify_6step_completed', { reg_type: [verificationType] }),
};

export const BetMatchEvents = {
	DATALAYER_BETMATCH_START: createEvent('betmatch_start'),
	DATALAYER_BETMATCH_START_1: createEvent('betmatch_start_1'),
	DATALAYER_BETMATCH_START_2: createEvent('betmatch_start_2'),
	DATALAYER_BETMATCH_MANUAL: createEvent('betmatch_manual'),
	DATALAYER_BETMATCH_SUCCESS: createEvent('betmatch_success'),
	DATALAYER_BETMATCH_FAILURE: createEvent('betmatch_failure'),
	DATALAYER_BETMATCH_CANCEL: createEvent('betmatch_cancel'),
	DATALAYER_BETMATCH_TIMEUP: createEvent('betmatch_timeup'),
	DATALAYER_BETMATCH_PLAY_AGAIN: createEvent('betmatch_play_again'),
	DATALAYER_BETMATCH_SWIPING: (mode: string, bet_name: string, bet_category: number) =>
		createEvent('betmatch_play', { mode: mode, bet_name: bet_name, bet_category: bet_category }),
};

export const InboxEvents = {
	DATALAYER_INBOX_OPEN: (source: string, source_category: string) =>
		createEvent('inbox_open', {
			source: source,
			source_category: source_category,
		}),

	DATALAYER_INBOX_OPEN_MESSAGE: (
		source: string,
		source_category: string,
		message_id: string,
		message_title: string,
		message_content: string,
		message_title_length: string,
		message_category: string,
		message_count: number,
		message_unread: number,
	) =>
		createEvent('inbox_open_message', {
			source: source,
			source_category: source_category,
			message_id: message_id,
			message_title: message_title,
			message_content: message_content,
			message_title_length: message_title_length,
			message_category: message_category,
			message_count: message_count,
			message_unread: message_unread,
		}),
	DATALAYER_PAGINATION_CHANGE: (source: string, source_category: string, page_number: number, message_count: number, message_unread: number) =>
		createEvent('inbox_message_change_page', {
			source: source,
			source_category: source_category,
			page_number: page_number,
			message_count: message_count,
			message_unread: message_unread,
		}),
	DATALAYER_MESSAGE_TOP_BANNER: (
		source: string,
		source_category: string,
		message_title: string,
		message_content: string,
		message_id: string,
		message_category: string,
	) =>
		createEvent('inbox_message_click_top_banner', {
			source: source,
			source_category: source_category,
			message_title: message_title,
			message_content: message_content,
			message_id: message_id,
			message_category: message_category,
		}),
	DATALAYER_MAIN_BANNER: (
		source: string,
		source_category: string,
		message_id: string,
		message_title: string,
		message_content: string,
		message_category: string,
	) =>
		createEvent('inbox_message_click_main_banner', {
			source: source,
			source_category: source_category,
			message_id: message_id,
			message_title: message_title,
			message_content: message_content,
			message_category: message_category,
		}),
	DATALAYER_INBOX_BUTTON_CLICK: (
		source: string,
		source_category: string,
		message_id: string,
		message_title: string,
		message_content: string,
		message_title_length: string,
		message_category: string,
	) =>
		createEvent('inbox_message_click_through', {
			source: source,
			source_category: source_category,
			message_id: message_id,
			message_title: message_title,
			message_content: message_content,
			message_title_length: message_title_length,
			message_category: message_category,
		}),
	DATALAYER_INBOX_BUTTON_CLICK_MORE: (
		source: string,
		source_category: string,
		message_id: string,
		message_title: string,
		message_content: string,
		message_title_length: string,
		message_category: string,
	) =>
		createEvent('inbox_message_click_more', {
			source: source,
			source_category: source_category,
			message_id: message_id,
			message_title: message_title,
			message_content: message_content,
			message_title_length: message_title_length,
			message_category: message_category,
		}),
	DATALAYER_OPEN_MESSAGE_LAST: (is_last: boolean) => createEvent('inbox_open_message_last', { is_last }),
};

export const LiveEvents = {
	DATALAYER_STREAM_START: <T>(attributes: T) => createEvent('stream_start', { attributes }),
	DATALAYER_STREAM_END: <T>(attributes: T) => createEvent('stream_end', { attributes }),
};

export const sendEventsToDataLayer = (arr: any, currentStep: number) => {
	setDataLayer(arr[currentStep - 1]);
};

export const SearchEvents = {
	DATALAYER_SEARCH_START: createEvent('search_start'),
	DATALAYER_SEARCH_SUCCESS: (search_value: string) => createEvent('search_success', { search_value }),
	DATALAYER_SEARCH_CANCEL: (search_cancel: string) => createEvent('search_cancel', { search_cancel }),
	DATALAYER_SEARCH_CLOSE: createEvent('search_close'),
};

export const copyCouponEvent = {
	DATALAYER_COPY_COUPON: (betslip_id: number) => createEvent('coupon_copy', { betslip_id }),
};

export const BannerEvent = {
	DATALAYER_BANNER_CLICK: (hp_banner_image_alt: string, hp_banner_position: number[]) =>
		createEvent('hp_banner_click', { hp_banner_image_alt: hp_banner_image_alt, hp_banner_position: hp_banner_position }),
};

export const betbuilderEvents = {
	DATALAYER_BETBUILDER_TEST_OLD: createEvent('old-view'),
	DATALAYER_BETBUILDER_TEST_NEW: createEvent('new-view'),
	DATALAYER_LEFT_BET_BUILDER_VIEW: createEvent('left-bet-builder-view'),
};

// Currently only mojeID for both. May be more of them in the future.
type THIRD_PARTY_REGISTRATION_TYPE = 'mojeID';
type THIRD_PARTY_VERIFICATION_TYPE = 'mojeID';

export const ThirdPartyRegistrationEvents = {
	// Before redirecting to Authologic
	REGISTER_THIRD_PARTY_IN: (thirdPartySessionId: string, type: THIRD_PARTY_REGISTRATION_TYPE) => ({
		event: 'reg_3rd_party_in',
		'3rd_party_type': [type],
		third_party_session_id: [thirdPartySessionId],
	}),

	// After coming back from Authologic
	REGISTER_THIRD_PARTY_OUT: (thirdPartySessionId: string, type: THIRD_PARTY_REGISTRATION_TYPE, alreadyRegistered: boolean) => ({
		event: 'reg_3rd_party_out',
		'3rd_party_type': [type],
		is_registered: [alreadyRegistered],
		third_party_session_id: [thirdPartySessionId],
	}),

	// After successful registration
	REGISTER_THIRD_PARTY_SUCCESS: (thirdPartySessionId: string, type: THIRD_PARTY_REGISTRATION_TYPE, promoCodeScratched: boolean) => ({
		event: 'reg_3rd_party_success',
		'3rd_party_type': [type],
		reg_scratch: [promoCodeScratched],
		third_party_session_id: [thirdPartySessionId],
	}),

	// After failed registration
	REGISTER_THIRD_PARTY_FAILED: (thirdPartySessionId: string, type: THIRD_PARTY_REGISTRATION_TYPE, reason: string) => ({
		event: 'reg_3rd_party_failed',
		'3rd_party_type': [type],
		fail_reason: [reason],
		third_party_session_id: [thirdPartySessionId],
	}),
};

export const ThirdPartyVerificationEvents = {
	// Before redirecting to Authologic
	VERIFY_THIRD_PARTY_IN: (thirdPartySessionId: string, type: THIRD_PARTY_VERIFICATION_TYPE) => ({
		event: 'verify_3rd_party_in',
		'3rd_party_type': [type],
		third_party_session_id: [thirdPartySessionId],
	}),

	// After coming back from Authologic
	VERIFY_THIRD_PARTY_OUT: (thirdPartySessionId: string, type: THIRD_PARTY_VERIFICATION_TYPE) => ({
		event: 'verify_3rd_party_out',
		'3rd_party_type': [type],
		third_party_session_id: [thirdPartySessionId],
	}),

	// After successful verification
	VERIFY_THIRD_PARTY_SUCCESS: (thirdPartySessionId: string, type: THIRD_PARTY_VERIFICATION_TYPE) => ({
		event: 'verify_3rd_party_success',
		'3rd_party_type': [type],
		third_party_session_id: [thirdPartySessionId],
	}),

	// After failed verification
	VERIFY_THIRD_PARTY_FAILED: (thirdPartySessionId: string, type: THIRD_PARTY_VERIFICATION_TYPE, reason: string) => ({
		event: 'verify_3rd_party_failed',
		'3rd_party_type': [type],
		fail_reason: [reason],
		third_party_session_id: [thirdPartySessionId],
	}),
};

export const AICampaignEvents = {
	DATALAYER_REGISTRATION_CAMPAIGN_SELECTION: (selected_option: AICampaignOption) => ({
		event: 'registration_campaign_selection',
		selected_option,
	}),
};

export const PlayerSurveyEvents = {
	NOT_VERIFIED: dataLayerPusher(DataLayerEventList.NotVerifiedPlayerSurvey),
};

export const RegisterPromotionPickerEvents = {
	CHOSEN_PROMOTION: dataLayerPusher(DataLayerEventList.PromotionChosenWhileRegistering),
};

export const BetEvents = {
	ERROR: dataLayerPusher(DataLayerEventList.BetError),
};

export const PaymentEvents = {
	DEPO_START: dataLayerPusher(DataLayerEventList.DepoStart),
};

export const BannerSliderEvents = {
	NOT_LOGGED_CLICK_BANNER: dataLayerPusher(DataLayerEventList.RegStart2),
	HOME_PAGE_BANNER_CLICK: dataLayerPusher(DataLayerEventList.HomePageBannerClick),
};
